import api from './api';
import { apiCache } from '../utils/cache';

const CACHE_TTL = 300; // 5 minutes

/**
 * Generate cache key from params
 */
const getCacheKey = (params) => {
  if (!params || Object.keys(params).length === 0) return null;
  
  // Only cache simple queries (featured, newest, category with no search)
  const { is_featured, sort, category_id, limit, page, search } = params;
  
  // Don't cache search queries or complex filters
  if (search || params.min_price || params.max_price) return null;
  
  if (is_featured && !category_id && !search) {
    return `products:featured:${limit || 20}`;
  }
  
  if (sort === '-created_at' && !is_featured && !category_id && !search) {
    return `products:newest:${limit || 20}`;
  }
  
  if (category_id && !search && page === 1) {
    return `products:category:${category_id}:${limit || 20}`;
  }
  
  return null;
};

export const productService = {
  // Public Methods
  getProducts: async (params = {}) => {
    const cacheKey = getCacheKey(params);
    
    if (cacheKey) {
      const cached = apiCache.get(cacheKey);
      if (cached) return cached;
    }
    
    const { data } = await api.get('/products/', { params });
    
    if (cacheKey) {
      apiCache.set(cacheKey, data, CACHE_TTL);
    }
    
    return data;
  },

  getProductBySlug: async (slug) => {
    const cacheKey = `product:slug:${slug}`;
    const cached = apiCache.get(cacheKey);
    if (cached) return cached;
    
    const { data } = await api.get(`/products/${slug}`);
    apiCache.set(cacheKey, data, CACHE_TTL);
    return data;
  },

  getProductById: async (id) => {
    const { data } = await api.get(`/products/by-id/${id}`);
    return data;
  },

  getFeaturedProducts: async () => {
    const cacheKey = 'products:featured:10';
    const cached = apiCache.get(cacheKey);
    if (cached) return cached;
    
    const { data } = await api.get('/products/featured');
    apiCache.set(cacheKey, data, CACHE_TTL);
    return data;
  },

  incrementViewCount: async (productId) => {
    const { data } = await api.post(`/products/${productId}/view`);
    return data;
  },

  searchProducts: async (query, limit = 10) => {
    // Don't cache search results
    const { data } = await api.get('/products/', { 
      params: { search: query, limit } 
    });
    return data;
  },

  // Admin Methods - invalidate caches
  createProduct: async (productData) => {
    const { data } = await api.post('/products/', productData);
    apiCache.deleteByPrefix('product');
    apiCache.deleteByPrefix('home');
    return data;
  },

  updateProduct: async (id, productData) => {
    const { data } = await api.put(`/products/${id}`, productData);
    apiCache.deleteByPrefix('product');
    apiCache.deleteByPrefix('home');
    return data;
  },

  deleteProduct: async (id) => {
    const { data } = await api.delete(`/products/${id}`);
    apiCache.deleteByPrefix('product');
    apiCache.deleteByPrefix('home');
    return data;
  },

  // Image Upload Methods
  uploadProductImage: async (productId, file, isPrimary = false) => {
    const formData = new FormData();
    formData.append('images', file);
    formData.append('is_primary', isPrimary);
    
    const { data } = await api.post(`/products/${productId}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  uploadProductImageFromUrl: async (productId, imageUrl, altText = '', isPrimary = false) => {
    const { data } = await api.post(`/products/${productId}/images/url`, {
      image_url: imageUrl,
      alt_text: altText,
      is_primary: isPrimary,
    });
    return data;
  },

  deleteProductImage: async (imageId) => {
    const { data } = await api.delete(`/products/images/${imageId}`);
    return data;
  },

  setImageAsPrimary: async (imageId) => {
    const { data } = await api.patch(`/products/images/${imageId}/primary`);
    return data;
  },

  // Quick upload - uploads to Cloudinary and returns URL (uses a temporary product)
  uploadImageToCloudinary: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  // Brand list for filter sidebar
  getBrands: async (categoryId = null) => {
    const params = categoryId ? { category_id: categoryId } : {};
    const { data } = await api.get('/products/brands', { params });
    return data; // string[]
  },
};

export default productService;
